import { logger } from '@/utils/logger';
import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import {
  Role,
  Permission,
  getPermissionsForRole,
  type RoleType,
  type PermissionType
} from '@/lib/rbac';

interface PermissionsResponse {
  role: string;
  permissions: string[];
  is_admin: boolean;
  is_verified: boolean;
  can_view_all_data: boolean;
  user_uid: string;
}

interface UserPermissions {
  role: string;
  isAdmin: boolean;
  isVerified: boolean;
  permissions: string[];
  canManageUsers: boolean;
  canManageVideos: boolean;
  canViewSystem: boolean;
  canManageSystem: boolean;
  canViewAllData: boolean;
  canReadAudit: boolean;
  canManageStorage: boolean;
  canReadHydropower: boolean;
}

const DEFAULT_PERMISSIONS: UserPermissions = {
  role: Role.USER,
  isAdmin: false,
  isVerified: false,
  permissions: [],
  canManageUsers: false,
  canManageVideos: false,
  canViewSystem: false,
  canManageSystem: false,
  canViewAllData: false,
  canReadAudit: false,
  canManageStorage: false,
  canReadHydropower: false,
};


const parsePermissionsFromToken = (): UserPermissions => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) return DEFAULT_PERMISSIONS;

    const base64Payload = token.split('.')[1];
    const padded = base64Payload + '='.repeat((4 - base64Payload.length % 4) % 4);
    const payload = JSON.parse(atob(padded));
    const userRole = (payload.user?.role || payload.role || Role.USER) as RoleType;

    // Get permissions from centralized RBAC config
    const rolePermissions = getPermissionsForRole(userRole);

    const isAdmin = userRole === Role.ADMIN;
    const isEngineer = userRole === Role.ENGINEER;
    const isExecutive = userRole === Role.EXECUTIVE;
    const canViewAllData = isAdmin || isEngineer;

    return {
      role: userRole,
      isAdmin,
      isVerified: true, // Assume verified if has token
      permissions: rolePermissions,
      canManageUsers: rolePermissions.includes(Permission.USERS_WRITE),
      canManageVideos: rolePermissions.includes(Permission.VIDEOS_WRITE),
      canViewSystem: rolePermissions.includes(Permission.SYSTEM_READ),
      canManageSystem: rolePermissions.includes(Permission.SYSTEM_WRITE),
      canViewAllData,
      canReadAudit: rolePermissions.includes(Permission.AUDIT_READ),
      canManageStorage: rolePermissions.includes(Permission.STORAGE_WRITE),
      canReadHydropower: rolePermissions.includes(Permission.HYDROPOWER_READ),
    };
  } catch {
    return DEFAULT_PERMISSIONS;
  }
};

export const useAdminPermissions = () => {
  const [permissions, setPermissions] = useState<UserPermissions>(DEFAULT_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setPermissions(DEFAULT_PERMISSIONS);
        setLoading(false);
        return;
      }

      const data = await apiClient.get('/v1/auth/permissions') as PermissionsResponse | undefined;

      if (!data || typeof data.role !== 'string' || !Array.isArray(data.permissions)) {
        logger.warn('Invalid permissions response, using token fallback', data);
        setPermissions(parsePermissionsFromToken());
        setError('Using cached permissions');
        return;
      }

      setPermissions({
        role: data.role,
        isAdmin: data.is_admin ?? false,
        isVerified: data.is_verified ?? false,
        permissions: data.permissions,
        canManageUsers: data.permissions.includes('users:write'),
        canManageVideos: data.permissions.includes('videos:write'),
        canViewSystem: data.permissions.includes('system:read'),
        canManageSystem: data.permissions.includes('system:write'),
        canViewAllData: data.can_view_all_data ?? false,
        canReadAudit: data.permissions.includes('audit:read'),
        canManageStorage: data.permissions.includes('storage:write'),
        canReadHydropower: data.permissions.includes('hydropower:read'),
      });
      setError(null);
    } catch (err) {
      logger.warn('Failed to fetch permissions from API, using token fallback:', err);
      // Fallback to JWT parsing if API fails
      setPermissions(parsePermissionsFromToken());
      setError('Using cached permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = (permission: PermissionType | string): boolean => {
    return permissions.permissions.includes(permission);
  };

  const hasAnyPermission = (perms: (PermissionType | string)[]): boolean => {
    return perms.some(p => permissions.permissions.includes(p));
  };

  const hasAllPermissions = (perms: (PermissionType | string)[]): boolean => {
    return perms.every(p => permissions.permissions.includes(p));
  };

  const requiresAdmin = (): boolean => {
    return !permissions.isAdmin;
  };

  // Role-specific checks
  const isRole = (role: RoleType | string): boolean => {
    return permissions.role === role;
  };

  const isAnyRole = (roles: (RoleType | string)[]): boolean => {
    return roles.includes(permissions.role);
  };

  return {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    requiresAdmin,
    isRole,
    isAnyRole,
    refetch: fetchPermissions,
    // Re-export Permission and Role for convenience
    Permission,
    Role,
  };
};
